import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { COUNTRIES } from "../lib/countries";
import ProtectedImage from "../components/ProtectedImage";
import { toast } from "sonner";
import {
  Monitor, MonitorOff, Download, FileText, ArrowLeft, Gamepad2,
  Wifi, Calendar as CalIcon, Check, AlertTriangle, Trophy, X,
  Users, Inbox, Clock, Send, MessageSquare, UserCheck, UserX,
  ListOrdered, Edit3, AlertCircle, Crown, Award,
  Pin, Trash2, Image as ImageIcon, PencilLine,
} from "lucide-react";
import { Link } from "react-router-dom";
import parsecGuide from "../data/parsecGuide.json";

// Steps: gate-pc → gate-parsec → guide → profile-completion → game-pick → role-pick → calendar → done
function Stepper({ children, ...rest }) {
  return <div className="space-y-6" {...rest}>{children}</div>;
}

function YesNoButton({ label, onClick, color, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 inline-flex items-center justify-center gap-2 border-2 px-6 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-colors ${
        color === "green"
          ? "border-[#00FF87] text-[#00FF87] hover:bg-[#00FF87] hover:text-black"
          : "border-[#71717A] text-[#71717A] hover:bg-[#71717A] hover:text-black"
      }`}
    >
      {label}
    </button>
  );
}

export default function Classics() {
  const { user, refreshMe } = useAuth();
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("loading");
  const [games, setGames] = useState([]);
  const [pickedGame, setPickedGame] = useState(null);
  const [pickedRole, setPickedRole] = useState(null);
  const [hostSpeedConfirmed, setHostSpeedConfirmed] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: prof }, { data: gameList }] = await Promise.all([
        api.get("/classics/profile/me"),
        api.get("/classics/games"),
      ]);
      setData(prof);
      setGames(gameList);
      // Decide initial step based on profile state
      if (!prof.profile) setStep("gate-pc");
      else if (!prof.profile.is_pc) setStep("blocked-non-pc");
      else if (!prof.profile.has_parsec) setStep("guide");
      else if (!prof.profile_complete) setStep("profile-completion");
      else setStep("dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  if (loading) return <TactLoader label={t("cl.loading")} />;
  if (!user || user === false) {
    return (
      <Panel testId="cl-anon" className="font-mono text-sm text-[#71717A]">
        [ {t("cl.signinToContinue")} ]
      </Panel>
    );
  }

  // ---------------- Step UIs ----------------
  if (step === "gate-pc") return <GatePC onAnswer={async (yes) => {
    if (!yes) {
      await api.post("/classics/qualify", { is_pc: false, has_parsec: false });
      setStep("blocked-non-pc"); return;
    }
    await api.post("/classics/qualify", { is_pc: true, has_parsec: false });
    setStep("gate-parsec");
  }} />;

  if (step === "blocked-non-pc") return <BlockedNonPC onBack={async () => { await api.post("/classics/qualify", { is_pc: false, has_parsec: false }); setData(null); setStep("gate-pc"); }} />;

  if (step === "gate-parsec") return <GateParsec onAnswer={async (yes) => {
    await api.post("/classics/qualify", { is_pc: true, has_parsec: yes });
    if (yes) {
      if (!data?.profile_complete) setStep("profile-completion");
      else setStep("dashboard");
    } else {
      setStep("guide");
    }
  }} />;

  if (step === "guide") return <ParsecGuide lang={lang} onClaimInstalled={async () => {
    await api.post("/classics/qualify", { is_pc: true, has_parsec: true });
    await refresh();
  }} />;

  if (step === "profile-completion") return <ProfileGate user={data?.user || user} onSaved={async () => { await refreshMe?.(); await refresh(); }} />;

  if (step === "dashboard" || step === "game-pick" || step === "role-pick") {
    return (
      <ClassicsDashboard
        profile={data?.profile}
        games={games}
        pickedGame={pickedGame}
        setPickedGame={setPickedGame}
        pickedRole={pickedRole}
        setPickedRole={setPickedRole}
        hostSpeedConfirmed={hostSpeedConfirmed}
        setHostSpeedConfirmed={setHostSpeedConfirmed}
        onJoined={refresh}
        onUpdated={refresh}
      />
    );
  }

  return <Panel className="font-mono text-sm text-[#71717A]">[ Unknown step: {step} ]</Panel>;
}

// ---- 1. PC gate ----
function GatePC({ onAnswer }) {
  const { t } = useT();
  return (
    <Stepper data-testid="cl-gate-pc">
      <CoverHero />
      <SectionTitle kicker={`// ${t("cl.gate.kicker")}`}>{t("cl.gate.q1")}</SectionTitle>
      <p className="font-body text-sm text-[#71717A] max-w-xl">{t("cl.gate.q1Help")}</p>
      <div className="flex gap-3 max-w-md">
        <YesNoButton label={t("common.yes")} color="green" onClick={() => onAnswer(true)} testId="cl-gate-pc-yes" />
        <YesNoButton label={t("common.no")} onClick={() => onAnswer(false)} testId="cl-gate-pc-no" />
      </div>
    </Stepper>
  );
}

function BlockedNonPC({ onBack }) {
  const { t } = useT();
  return (
    <Stepper>
      <CoverHero />
      <Panel className="border-l-2 border-l-[#EA580C]" testId="cl-blocked">
        <div className="flex items-start gap-3">
          <MonitorOff className="w-6 h-6 text-[#EA580C] mt-1" />
          <div>
            <h3 className="font-heading text-2xl uppercase mb-2">{t("cl.blocked.title")}</h3>
            <p className="font-body text-sm text-[#09090B] dark:text-[#F4F4F5] leading-relaxed">{t("cl.blocked.body")}</p>
            <button onClick={onBack} className="mt-4 inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors" data-testid="cl-blocked-back">
              <ArrowLeft className="w-3 h-3" /> {t("cl.blocked.back")}
            </button>
          </div>
        </div>
      </Panel>
    </Stepper>
  );
}

// ---- 2. Parsec gate ----
function GateParsec({ onAnswer }) {
  const { t } = useT();
  return (
    <Stepper data-testid="cl-gate-parsec">
      <CoverHero />
      <SectionTitle kicker={`// ${t("cl.gate.kicker")}`}>{t("cl.gate.q2")}</SectionTitle>
      <p className="font-body text-sm text-[#71717A] max-w-xl">{t("cl.gate.q2Help")}</p>
      <div className="flex gap-3 max-w-md">
        <YesNoButton label={t("common.yes")} color="green" onClick={() => onAnswer(true)} testId="cl-gate-parsec-yes" />
        <YesNoButton label={t("common.no")} onClick={() => onAnswer(false)} testId="cl-gate-parsec-no" />
      </div>
    </Stepper>
  );
}

// ---- 3. Parsec install guide (bilingual) ----
function ParsecGuide({ lang, onClaimInstalled }) {
  const { t } = useT();
  const [docLang, setDocLang] = useState(lang === "ar" ? "ar" : "en");
  const blocks = parsecGuide[docLang] || parsecGuide.en;
  return (
    <Stepper data-testid="cl-guide">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionTitle kicker={`// ${t("cl.guide.kicker")}`}>{t("cl.guide.title")}</SectionTitle>
        <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1">
          {[{id:"en",label:"EN"},{id:"ar",label:"AR"}].map((l) => (
            <button key={l.id} onClick={() => setDocLang(l.id)} data-testid={`cl-guide-lang-${l.id}`} className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${docLang === l.id ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"}`}>{l.label}</button>
          ))}
        </div>
      </div>

      <Panel testId="cl-guide-content" className={docLang === "ar" ? "rtl" : ""}>
        <div dir={docLang === "ar" ? "rtl" : "ltr"} className={`font-body text-sm text-[#09090B] dark:text-[#F4F4F5] leading-relaxed space-y-2 ${docLang === "ar" ? "text-right" : ""}`}>
          {blocks.map((b, i) => {
            if (b.type === "h1") return <h2 key={i} className="font-heading text-2xl uppercase tracking-wider mt-3 mb-1">{b.text}</h2>;
            if (b.type === "h2") return <h3 key={i} className="font-heading text-lg uppercase tracking-wider mt-3 mb-1 text-[#7C3AED]">{b.text}</h3>;
            if (b.type === "h3") return <h4 key={i} className="font-bold mt-2">{b.text}</h4>;
            if (b.type === "li") return <div key={i} className="ml-4 flex gap-2"><span className="text-[#7C3AED]">•</span><span>{b.text}</span></div>;
            return <p key={i}>{b.text}</p>;
          })}
        </div>
      </Panel>

      <div className="flex flex-wrap gap-2">
        <a href="/classics/parsec_guide_en.docx" download data-testid="cl-guide-download-en" className="inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
          <Download className="w-3 h-3" /> {t("cl.guide.downloadEn")}
        </a>
        <a href="/classics/parsec_guide_ar.docx" download data-testid="cl-guide-download-ar" className="inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
          <Download className="w-3 h-3" /> {t("cl.guide.downloadAr")}
        </a>
        <a href="https://parsec.app/downloads" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:border-[#7C3AED] hover:text-[#7C3AED] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
          <FileText className="w-3 h-3" /> parsec.app
        </a>
        <button onClick={onClaimInstalled} data-testid="cl-guide-installed" className="inline-flex items-center gap-2 bg-[#00FF87] text-black hover:bg-[#00E576] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
          <Check className="w-3 h-3" /> {t("cl.guide.installed")}
        </button>
      </div>
    </Stepper>
  );
}

// ---- 4. Profile completion gate (nationality + age) ----
function ProfileGate({ user, onSaved }) {
  const { t } = useT();
  const [nat, setNat] = useState(user?.nationality || "");
  const [age, setAge] = useState(user?.age || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!nat) { toast.error(t("cl.profile.errNat")); return; }
    const ageNum = parseInt(age, 10);
    if (!ageNum || ageNum < 1 || ageNum > 120) { toast.error(t("cl.profile.errAge")); return; }
    setBusy(true);
    try {
      await api.patch("/users/me/profile", { nationality: nat, age: ageNum });
      toast.success(t("pc.saved"));
      await onSaved?.();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <Stepper data-testid="cl-profile-gate">
      <SectionTitle kicker={`// ${t("cl.profile.kicker")}`}>{t("cl.profile.title")}</SectionTitle>
      <p className="font-body text-sm text-[#71717A] max-w-xl">{t("cl.profile.body")}</p>
      <Panel testId="cl-profile-form">
        <form onSubmit={submit} className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("pc.nationality")}</label>
            <select value={nat} onChange={(e) => setNat(e.target.value)} required data-testid="cl-profile-nationality" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]">
              <option value="">{t("pc.nationalityPh")}</option>
              {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("cl.profile.age")}</label>
            <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="1" max="120" data-testid="cl-profile-age" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]" />
          </div>
          <button type="submit" disabled={busy} data-testid="cl-profile-save" className="bg-[#7C3AED] text-white px-4 py-2 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#6D28D9] transition-colors">
            {busy ? t("pc.saving") : t("pc.save")}
          </button>
        </form>
      </Panel>
    </Stepper>
  );
}

// ---- 5. Dashboard with games list, role pick, calendar ----
function ClassicsDashboard({ profile, games, pickedGame, setPickedGame, pickedRole, setPickedRole, hostSpeedConfirmed, setHostSpeedConfirmed, onJoined, onUpdated }) {
  const { t } = useT();
  const myGames = profile?.games || [];

  // Sub-views: "list" (default), "join" (picking role for a game), "settings", "browse" (game-specific players/requests/matches)
  const [view, setView] = useState("list");
  const [browseGame, setBrowseGame] = useState(null);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [], resolved: [] });
  const [matches, setMatches] = useState({ upcoming: [], past: [] });
  const [familyFilter, setFamilyFilter] = useState("all"); // "all" | "pes" | "fifa"

  const refreshSocial = async () => {
    try {
      const [{ data: rq }, { data: ms }] = await Promise.all([
        api.get("/classics/requests"),
        api.get("/classics/matches"),
      ]);
      setRequests(rq);
      setMatches(ms);
    } catch { /* noop */ }
  };

  useEffect(() => { refreshSocial(); /* eslint-disable-next-line */ }, []);

  if (view === "join" && pickedGame) return (
    <RolePicker
      game={pickedGame}
      pickedRole={pickedRole}
      setPickedRole={setPickedRole}
      hostSpeedConfirmed={hostSpeedConfirmed}
      setHostSpeedConfirmed={setHostSpeedConfirmed}
      onCancel={() => { setPickedGame(null); setPickedRole(null); setHostSpeedConfirmed(false); setView("list"); }}
      onConfirm={async () => {
        try {
          await api.post(`/classics/games/${pickedGame.id}/join`, { role: pickedRole, confirmed_speed: hostSpeedConfirmed });
          toast.success(t("cl.join.toast", { name: pickedGame.name, role: pickedRole.toUpperCase() }));
          setPickedGame(null); setPickedRole(null); setHostSpeedConfirmed(false);
          setView("list");
          onJoined?.();
        } catch (e) { toast.error(formatApiError(e)); }
      }}
    />
  );

  if (view === "settings") return (
    <ClassicsSettings profile={profile} onSaved={() => { onUpdated?.(); setView("list"); }} onCancel={() => setView("list")} />
  );

  if (view === "browse" && browseGame) return (
    <ClassicsBrowse
      game={browseGame}
      onBack={() => { setBrowseGame(null); setView("list"); refreshSocial(); }}
      onAnyChange={refreshSocial}
    />
  );

  return (
    <Stepper data-testid="cl-dashboard">
      <CoverHero compact />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle kicker={`// ${t("cl.dashboard.kicker")}`}>{t("cl.dashboard.title")}</SectionTitle>
        <button type="button" onClick={() => setView("settings")} data-testid="cl-open-settings" className="inline-flex items-center gap-2 border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:border-[#7C3AED] hover:text-[#7C3AED] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
          <CalIcon className="w-3 h-3" /> {t("cl.dashboard.settings")}
        </button>
      </div>

      {/* Pending requests inbox alert */}
      {requests.incoming.length > 0 && (
        <Panel testId="cl-incoming-summary" className="border-l-2 border-l-[#EA580C]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Inbox className="w-5 h-5 text-[#EA580C]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">{t("cl.requests.kicker")}</div>
                <div className="font-heading text-lg uppercase tracking-wider">{t("cl.requests.pendingCount", { n: requests.incoming.length })}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {requests.incoming.slice(0, 3).map((r) => (
                <button
                  key={r.id}
                  onClick={() => { const g = games.find((x) => x.id === r.game_id); if (g) { setBrowseGame(g); setView("browse"); } }}
                  data-testid={`cl-incoming-jump-${r.id}`}
                  className="border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 transition-colors"
                >
                  {r.peer?.username} — {r.game?.name}
                </button>
              ))}
            </div>
          </div>
        </Panel>
      )}

      <Panel testId="cl-my-memberships">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-3">{t("cl.dashboard.mine")}</div>
        {myGames.length === 0 ? (
          <div className="font-mono text-sm text-[#71717A]">[ {t("cl.dashboard.noGames")} ]</div>
        ) : (
          <ul className="space-y-1.5" data-testid="cl-my-games-list">
            {myGames.map((g) => {
              const meta = games.find((x) => x.id === g.game_id);
              return (
                <li key={g.game_id} className="flex items-center justify-between gap-3 px-3 py-2 border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`cl-my-game-${g.game_id}`}>
                  <button
                    type="button"
                    onClick={() => { if (meta) { setBrowseGame(meta); setView("browse"); } }}
                    data-testid={`cl-browse-${g.game_id}`}
                    className="flex items-center gap-3 hover:text-[#7C3AED] transition-colors text-left flex-1"
                  >
                    <Trophy className="w-4 h-4 text-[#00FF87]" />
                    <div>
                      <div className="font-bold">{meta?.name || g.game_id}</div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#7C3AED]">{g.role} — {t("cl.browse.openCta")}</div>
                    </div>
                  </button>
                  <button type="button" onClick={async () => { try { await api.delete(`/classics/games/${g.game_id}`); toast.success(t("cl.leave.toast", { name: meta?.name || g.game_id })); onUpdated?.(); } catch (e) { toast.error(formatApiError(e)); } }} data-testid={`cl-leave-${g.game_id}`} className="border border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white font-mono text-[9px] uppercase tracking-[0.3em] px-2 py-1 transition-colors">
                    <X className="inline w-3 h-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel testId="cl-game-list">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("cl.dashboard.pickGame")}</div>
          <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1" data-testid="cl-family-filter">
            {[
              { id: "all", label: t("cl.family.all") },
              { id: "pes", label: "PES" },
              { id: "fifa", label: "FIFA" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFamilyFilter(f.id)}
                data-testid={`cl-family-${f.id}`}
                className={`px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${familyFilter === f.id ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-2" data-testid="cl-games-grid">
          {games.filter((g) => familyFilter === "all" || g.family === familyFilter).map((g) => {
            const joined = myGames.some((x) => x.game_id === g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => {
                  if (joined) {
                    // Joined game → open browse view directly.
                    setBrowseGame(g); setView("browse");
                  } else {
                    setPickedGame(g); setPickedRole(null); setHostSpeedConfirmed(false); setView("join");
                  }
                }}
                data-testid={`cl-game-${g.id}`}
                className={`border p-3 text-left hover:border-[#7C3AED] transition-colors group ${joined ? "border-[#00FF87] bg-[#00FF87]/5" : "border-[#E4E4E7] dark:border-[#2A2A30]"}`}
              >
                <div className="flex items-center justify-between">
                  <Gamepad2 className={`w-4 h-4 ${joined ? "text-[#00FF87]" : "text-[#7C3AED]"}`} />
                  {joined && <Check className="w-3 h-3 text-[#00FF87]" />}
                </div>
                <div className="mt-2 font-heading text-lg uppercase tracking-wider">{g.name}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#71717A]">{g.year}</div>
              </button>
            );
          })}
        </div>
      </Panel>

      {/* Upcoming Classics matches mini-list */}
      <Panel testId="cl-upcoming-matches">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-3">{t("cl.matches.upcomingKicker")}</div>
        {matches.upcoming.length === 0 ? (
          <div className="font-mono text-sm text-[#71717A]">[ {t("cl.matches.noUpcoming")} ]</div>
        ) : (
          <ul className="space-y-1.5" data-testid="cl-upcoming-list">
            {matches.upcoming.slice(0, 5).map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 px-3 py-2 border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`cl-upcoming-${m.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Clock className="w-4 h-4 text-[#7C3AED] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-bold truncate">{m.peer?.username}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{m.game?.name} · {formatProposedAt(m.scheduled_at, t)}</div>
                  </div>
                </div>
                <Link to={`/chat/${m.peer?.id}`} data-testid={`cl-upcoming-chat-${m.id}`} className="inline-flex items-center gap-1 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[9px] uppercase tracking-[0.3em] px-2 py-1 transition-colors">
                  <MessageSquare className="w-3 h-3" /> {t("cl.matches.chat")}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </Stepper>
  );
}

// ----  Format helper for proposed_at (ISO or freeform) ----
function formatProposedAt(s, t) {
  if (!s) return t("cl.matches.anytime");
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ---- Role picker ----
function RolePicker({ game, pickedRole, setPickedRole, hostSpeedConfirmed, setHostSpeedConfirmed, onConfirm, onCancel }) {
  const { t } = useT();
  return (
    <Stepper data-testid="cl-role-picker">
      <button type="button" onClick={onCancel} data-testid="cl-role-back" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#7C3AED] transition-colors">
        <ArrowLeft className="w-3 h-3" /> {t("cl.role.back")}
      </button>
      <SectionTitle kicker={`// ${game.name.toUpperCase()}`}>{t("cl.role.title")}</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button type="button" onClick={() => setPickedRole("host")} data-testid="cl-role-host" className={`border-2 p-5 text-left transition-colors ${pickedRole === "host" ? "border-[#7C3AED] bg-[#7C3AED]/10" : "border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#7C3AED]"}`}>
          <Wifi className="w-5 h-5 text-[#7C3AED] mb-2" />
          <div className="font-heading text-xl uppercase tracking-wider mb-1">{t("cl.role.host")}</div>
          <p className="font-body text-xs text-[#71717A]">{t("cl.role.hostHelp")}</p>
        </button>
        <button type="button" onClick={() => setPickedRole("guest")} data-testid="cl-role-guest" className={`border-2 p-5 text-left transition-colors ${pickedRole === "guest" ? "border-[#00FF87] bg-[#00FF87]/10" : "border-[#E4E4E7] dark:border-[#2A2A30] hover:border-[#00FF87]"}`}>
          <Monitor className="w-5 h-5 text-[#00FF87] mb-2" />
          <div className="font-heading text-xl uppercase tracking-wider mb-1">{t("cl.role.guest")}</div>
          <p className="font-body text-xs text-[#71717A]">{t("cl.role.guestHelp")}</p>
        </button>
      </div>

      {pickedRole === "host" && (
        <Panel testId="cl-host-disclaimer" className="border-l-2 border-l-[#EA580C]">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[#EA580C] mt-0.5 flex-shrink-0" />
            <div className="font-body text-sm">{t("cl.role.hostDisclaimer")}</div>
          </div>
          <a href="https://fast.com" target="_blank" rel="noopener noreferrer" data-testid="cl-host-speedtest" className="inline-flex items-center gap-2 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors">
            <Wifi className="w-3 h-3" /> {t("cl.role.speedtestBtn")}
          </a>
          <label className="mt-3 flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={hostSpeedConfirmed} onChange={(e) => setHostSpeedConfirmed(e.target.checked)} data-testid="cl-host-confirm" className="mt-1" />
            <span className="font-body text-sm">{t("cl.role.hostConfirm")}</span>
          </label>
        </Panel>
      )}
      {pickedRole === "guest" && (
        <Panel testId="cl-guest-disclaimer" className="border-l-2 border-l-[#00FF87]">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#00FF87] mt-0.5 flex-shrink-0" />
            <div className="font-body text-sm">{t("cl.role.guestDisclaimer")}</div>
          </div>
        </Panel>
      )}

      <button
        type="button"
        onClick={onConfirm}
        disabled={!pickedRole || (pickedRole === "host" && !hostSpeedConfirmed)}
        data-testid="cl-role-confirm"
        className="bg-[#7C3AED] text-white px-6 py-3 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#6D28D9] transition-colors"
      >
        {t("cl.role.join", { name: game.name })}
      </button>
    </Stepper>
  );
}

// ---- Settings: Parsec username + weekly availability ----
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function ClassicsSettings({ profile, onSaved, onCancel }) {
  const { t } = useT();
  const [parsec, setParsec] = useState(profile?.parsec_username || "");
  const [tz, setTz] = useState(profile?.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || ""));
  const [slots, setSlots] = useState(profile?.availability || []);
  const [busy, setBusy] = useState(false);

  // Build a quick lookup so checkbox state is derived
  const has = (day, hour) => slots.some((s) => s.day_of_week === day && hour >= s.start_hour && hour < s.end_hour);

  // Toggle a single hour-slot. We collapse contiguous hours into one slot on save.
  const toggle = (day, hour) => {
    if (has(day, hour)) {
      // Remove this hour: split any encompassing slot into 0..2 pieces
      const next = [];
      for (const s of slots) {
        if (s.day_of_week !== day || hour < s.start_hour || hour >= s.end_hour) {
          next.push(s);
          continue;
        }
        if (s.start_hour < hour) next.push({ day_of_week: day, start_hour: s.start_hour, end_hour: hour });
        if (hour + 1 < s.end_hour) next.push({ day_of_week: day, start_hour: hour + 1, end_hour: s.end_hour });
      }
      setSlots(next);
    } else {
      // Add this hour as a 1-hour slot; will be merged on save normalization
      setSlots([...slots, { day_of_week: day, start_hour: hour, end_hour: hour + 1 }]);
    }
  };

  const normalize = (arr) => {
    // Merge adjacent slots per day
    const byDay = {};
    arr.forEach((s) => { (byDay[s.day_of_week] ||= []).push(s); });
    const out = [];
    Object.keys(byDay).sort().forEach((d) => {
      const sorted = byDay[d].sort((a, b) => a.start_hour - b.start_hour);
      let cur = null;
      for (const s of sorted) {
        if (!cur) { cur = { ...s }; continue; }
        if (s.start_hour <= cur.end_hour) cur.end_hour = Math.max(cur.end_hour, s.end_hour);
        else { out.push(cur); cur = { ...s }; }
      }
      if (cur) out.push(cur);
    });
    return out;
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        parsec_username: parsec.trim() || null,
        timezone: tz.trim() || null,
        availability: normalize(slots),
      };
      await api.patch("/classics/profile/me", payload);
      toast.success(t("cl.settings.saved"));
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Stepper data-testid="cl-settings">
      <button type="button" onClick={onCancel} data-testid="cl-settings-back" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#7C3AED] transition-colors">
        <ArrowLeft className="w-3 h-3" /> {t("common.back")}
      </button>
      <SectionTitle kicker={`// ${t("cl.settings.kicker")}`}>{t("cl.settings.title")}</SectionTitle>

      <Panel testId="cl-settings-parsec">
        <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("cl.settings.parsec")}</label>
        <input value={parsec} onChange={(e) => setParsec(e.target.value)} required data-testid="cl-settings-parsec-input" placeholder={t("cl.settings.parsecPh")} className="w-full max-w-md bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]" />
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#71717A] mt-2">{t("cl.settings.parsecHelp")}</p>
      </Panel>

      <Panel testId="cl-settings-availability">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("cl.settings.calKicker")}</div>
            <div className="font-heading text-xl uppercase tracking-wider mt-1">{t("cl.settings.calTitle")}</div>
          </div>
          <div className="font-mono text-[10px] text-[#71717A]" data-testid="cl-settings-tz">{tz || "—"}</div>
        </div>
        <p className="font-body text-xs text-[#71717A] mb-3">{t("cl.settings.calHelp")}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono border-collapse" data-testid="cl-settings-cal-table">
            <thead>
              <tr>
                <th className="p-1 text-[#71717A] uppercase tracking-[0.2em] text-[10px]">H</th>
                {DAY_KEYS.map((dk, i) => (
                  <th key={i} className="p-1 text-[#71717A] uppercase tracking-[0.2em] text-[10px]">{t(`cl.settings.day.${dk}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HOURS.map((h) => (
                <tr key={h}>
                  <td className="p-0.5 text-[10px] text-[#71717A] text-center">{String(h).padStart(2, "0")}</td>
                  {DAY_KEYS.map((_, di) => {
                    const active = has(di, h);
                    return (
                      <td key={di} className="p-0">
                        <button
                          type="button"
                          onClick={() => toggle(di, h)}
                          data-testid={`cl-cal-${di}-${h}`}
                          className={`w-full h-6 border ${active ? "border-[#00FF87] bg-[#00FF87]/30" : "border-[#E4E4E7] dark:border-[#2A2A30] hover:bg-[#7C3AED]/15"} transition-colors`}
                          title={`${t(`cl.settings.day.${DAY_KEYS[di]}`)} ${String(h).padStart(2, "0")}:00`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <button type="button" onClick={save} disabled={busy} data-testid="cl-settings-save" className="bg-[#7C3AED] text-white px-6 py-3 font-mono text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-[#6D28D9] transition-colors">
        {busy ? t("cl.settings.saving") : t("cl.settings.save")}
      </button>
    </Stepper>
  );
}

function CoverHero({ compact }) {
  const { t } = useT();
  return (
    <section className={`relative overflow-hidden border border-[#E4E4E7] dark:border-[#2A2A30] ${compact ? "" : "mb-1"}`} data-testid="cl-cover">
      <img src="/classics/cover.jpg" alt="Classics league" className={`w-full object-cover ${compact ? "h-32 sm:h-40" : "h-48 sm:h-64"}`} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
      <div className={`absolute left-4 ${compact ? "bottom-3" : "bottom-6"} text-white`}>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF87]">// {t("cl.kicker")}</div>
        <h1 className={`font-heading uppercase tracking-wider drop-shadow-[0_0_18px_rgba(0,255,135,0.3)] ${compact ? "text-2xl sm:text-3xl" : "text-4xl sm:text-5xl"}`}>{t("cl.title")}</h1>
        {!compact && <p className="mt-1 max-w-xl font-body text-white/80 text-sm">{t("cl.subtitle")}</p>}
      </div>
    </section>
  );
}

// ====================================================================
// CLASSICS BROWSE — game-specific view: Players / Requests / Matches.
// ====================================================================
function ClassicsBrowse({ game, onBack, onAnyChange }) {
  const { t } = useT();
  const [tab, setTab] = useState("players");
  const [players, setPlayers] = useState([]);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [], resolved: [] });
  const [matches, setMatches] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [requestForPlayer, setRequestForPlayer] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: pl }, { data: rq }, { data: ms }] = await Promise.all([
        api.get(`/classics/games/${game.id}/players`),
        api.get("/classics/requests"),
        api.get("/classics/matches"),
      ]);
      setPlayers(pl || []);
      setRequests(rq);
      setMatches(ms);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [game?.id]);

  const gameIncoming = (requests.incoming || []).filter((r) => r.game_id === game.id);
  const gameOutgoing = (requests.outgoing || []).filter((r) => r.game_id === game.id);
  const gameMatches = (matches.upcoming || []).filter((m) => m.game_id === game.id);
  const gameDisputed = (matches.disputed || []).filter((m) => m.game_id === game.id);
  const gamePast = (matches.past || []).filter((m) => m.game_id === game.id);
  const pendingPeerIds = new Set([
    ...gameIncoming.map((r) => r.peer?.id),
    ...gameOutgoing.map((r) => r.peer?.id),
  ]);

  return (
    <Stepper data-testid="cl-browse">
      <button type="button" onClick={onBack} data-testid="cl-browse-back" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] hover:text-[#7C3AED] transition-colors">
        <ArrowLeft className="w-3 h-3" /> {t("cl.browse.back")}
      </button>
      <SectionTitle kicker={`// ${game.name.toUpperCase()}`}>{t("cl.browse.title")}</SectionTitle>

      <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1 w-fit overflow-x-auto no-scrollbar" data-testid="cl-browse-tabs">
        {[
          { id: "players", label: t("cl.browse.tabPlayers"), icon: Users, count: players.length },
          { id: "requests", label: t("cl.browse.tabRequests"), icon: Inbox, count: gameIncoming.length + gameOutgoing.length },
          { id: "matches", label: t("cl.browse.tabMatches"), icon: Trophy, count: gameMatches.length + gameDisputed.length },
          { id: "leaderboard", label: t("cl.browse.tabLeaderboard"), icon: ListOrdered, count: 0 },
          { id: "chat", label: t("cl.browse.tabChat"), icon: MessageSquare, count: 0 },
        ].map((tt) => (
          <button
            key={tt.id}
            onClick={() => setTab(tt.id)}
            data-testid={`cl-browse-tab-${tt.id}`}
            className={`inline-flex items-center gap-2 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${tab === tt.id ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"}`}
          >
            <tt.icon className="w-3 h-3" /> {tt.label} {tt.count > 0 && <span className={`ml-1 px-1.5 py-0.5 text-[9px] ${tab === tt.id ? "bg-white/20" : "bg-[#7C3AED]/15 text-[#7C3AED]"}`}>{tt.count}</span>}
          </button>
        ))}
      </div>

      {loading && <TactLoader label={t("cl.browse.loading")} />}

      {!loading && tab === "players" && (
        <PlayersList players={players} pendingPeerIds={pendingPeerIds} onRequest={setRequestForPlayer} />
      )}

      {!loading && tab === "requests" && (
        <RequestsView
          incoming={gameIncoming}
          outgoing={gameOutgoing}
          onAccept={async (id) => { try { await api.post(`/classics/requests/${id}/accept`); toast.success(t("cl.requests.acceptedToast")); await refresh(); onAnyChange?.(); } catch (e) { toast.error(formatApiError(e)); } }}
          onDecline={async (id) => { try { await api.post(`/classics/requests/${id}/decline`); toast.success(t("cl.requests.declinedToast")); await refresh(); onAnyChange?.(); } catch (e) { toast.error(formatApiError(e)); } }}
          onCancel={async (id) => { try { await api.post(`/classics/requests/${id}/cancel`); toast.success(t("cl.requests.cancelledToast")); await refresh(); onAnyChange?.(); } catch (e) { toast.error(formatApiError(e)); } }}
        />
      )}

      {!loading && tab === "matches" && <MatchesList matches={gameMatches} disputed={gameDisputed} past={gamePast} onChanged={refresh} />}

      {!loading && tab === "leaderboard" && <LeaderboardView gameId={game.id} />}

      {!loading && tab === "chat" && <ClassicsChatView game={game} />}

      {requestForPlayer && (
        <RequestModal
          player={requestForPlayer}
          game={game}
          onClose={() => setRequestForPlayer(null)}
          onSent={async () => { setRequestForPlayer(null); await refresh(); onAnyChange?.(); }}
        />
      )}
    </Stepper>
  );
}

function PlayersList({ players, pendingPeerIds, onRequest }) {
  const { t } = useT();
  if (players.length === 0) {
    return (
      <Panel testId="cl-players-empty">
        <div className="font-mono text-sm text-[#71717A]">[ {t("cl.browse.noPlayers")} ]</div>
      </Panel>
    );
  }
  return (
    <ul className="space-y-2" data-testid="cl-players-list">
      {players.map((p) => {
        const hasPending = pendingPeerIds.has(p.user_id);
        return (
          <li key={p.user_id} className={`flex items-center justify-between gap-3 px-3 py-3 border ${p.role === "host" ? "border-[#7C3AED]" : "border-[#00FF87]"}`} data-testid={`cl-player-${p.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-[#7C3AED]/15 flex items-center justify-center font-bold text-[#7C3AED] flex-shrink-0">
                {(p.username || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="font-bold truncate">
                  <Link to={`/players/${p.username}`} className="hover:text-[#7C3AED] transition-colors">{p.username}</Link>
                  {p.club_name && <span className="ml-2 font-mono text-[10px] text-[#71717A]">{p.club_name}</span>}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 flex-wrap mt-0.5">
                  <span className={p.role === "host" ? "text-[#7C3AED]" : "text-[#00FF87]"}>
                    {p.role === "host" ? <><Wifi className="inline w-3 h-3 mr-1" /> {t("cl.role.host")}</> : <><Monitor className="inline w-3 h-3 mr-1" /> {t("cl.role.guest")}</>}
                  </span>
                  {p.parsec_username && <span className="text-[#71717A]">parsec: <span className="text-[#09090B] dark:text-[#F4F4F5]">{p.parsec_username}</span></span>}
                  {p.timezone && <span className="text-[#71717A]">{p.timezone}</span>}
                  {p.availability && p.availability.length > 0 && <span className="text-[#71717A]">· {p.availability.length} {t("cl.browse.slots")}</span>}
                </div>
              </div>
            </div>
            <button
              type="button"
              disabled={hasPending}
              onClick={() => onRequest(p)}
              data-testid={`cl-request-${p.user_id}`}
              className="inline-flex items-center gap-1 bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-40 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-2 transition-colors flex-shrink-0"
            >
              <Send className="w-3 h-3" /> {hasPending ? t("cl.browse.pendingBtn") : t("cl.browse.requestBtn")}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function RequestsView({ incoming, outgoing, onAccept, onDecline, onCancel }) {
  const { t } = useT();
  return (
    <div className="space-y-4" data-testid="cl-requests-view">
      <Panel testId="cl-requests-incoming">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C] mb-3">
          {t("cl.requests.incomingKicker")} {incoming.length > 0 && <span>({incoming.length})</span>}
        </div>
        {incoming.length === 0 ? (
          <div className="font-mono text-sm text-[#71717A]">[ {t("cl.requests.noIncoming")} ]</div>
        ) : (
          <ul className="space-y-2">
            {incoming.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3 p-3 border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`cl-incoming-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-bold">{r.peer?.username}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    {r.proposed_at ? formatProposedAt(r.proposed_at, t) : t("cl.matches.anytime")}
                  </div>
                  {r.message && <div className="text-sm mt-1.5 whitespace-pre-wrap">{r.message}</div>}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => onAccept(r.id)} data-testid={`cl-accept-${r.id}`} className="inline-flex items-center gap-1 bg-[#00FF87] text-black hover:bg-[#00E576] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 transition-colors">
                    <UserCheck className="w-3 h-3" /> {t("cl.requests.accept")}
                  </button>
                  <button onClick={() => onDecline(r.id)} data-testid={`cl-decline-${r.id}`} className="inline-flex items-center gap-1 border border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 transition-colors">
                    <UserX className="w-3 h-3" /> {t("cl.requests.decline")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel testId="cl-requests-outgoing">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-3">
          {t("cl.requests.outgoingKicker")} {outgoing.length > 0 && <span>({outgoing.length})</span>}
        </div>
        {outgoing.length === 0 ? (
          <div className="font-mono text-sm text-[#71717A]">[ {t("cl.requests.noOutgoing")} ]</div>
        ) : (
          <ul className="space-y-2">
            {outgoing.map((r) => (
              <li key={r.id} className="flex items-start justify-between gap-3 p-3 border border-[#E4E4E7] dark:border-[#2A2A30]" data-testid={`cl-outgoing-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-bold">{r.peer?.username}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    {r.proposed_at ? formatProposedAt(r.proposed_at, t) : t("cl.matches.anytime")}
                  </div>
                  {r.message && <div className="text-sm mt-1.5 whitespace-pre-wrap">{r.message}</div>}
                </div>
                <button onClick={() => onCancel(r.id)} data-testid={`cl-cancel-${r.id}`} className="inline-flex items-center gap-1 border border-[#71717A] text-[#71717A] hover:border-[#E11D48] hover:text-[#E11D48] font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 transition-colors flex-shrink-0">
                  <X className="w-3 h-3" /> {t("cl.requests.cancel")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function MatchesList({ matches, disputed = [], past = [], onChanged }) {
  const { t } = useT();
  const [scoreFor, setScoreFor] = useState(null);
  if (matches.length === 0 && disputed.length === 0 && past.length === 0) {
    return (
      <Panel testId="cl-matches-empty">
        <div className="font-mono text-sm text-[#71717A]">[ {t("cl.matches.noUpcoming")} ]</div>
      </Panel>
    );
  }
  return (
    <div className="space-y-4" data-testid="cl-matches-view">
      {disputed.length > 0 && (
        <Panel testId="cl-matches-disputed" className="border-l-2 border-l-[#E11D48]">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48] mb-3">{t("cl.matches.disputedKicker")} ({disputed.length})</div>
          <ul className="space-y-2">
            {disputed.map((m) => <MatchRow key={m.id} m={m} onSubmitScore={() => setScoreFor(m)} variant="disputed" />)}
          </ul>
        </Panel>
      )}
      {matches.length > 0 && (
        <Panel testId="cl-matches-upcoming">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-3">{t("cl.matches.acceptedKicker")} ({matches.length})</div>
          <ul className="space-y-2" data-testid="cl-matches-list">
            {matches.map((m) => <MatchRow key={m.id} m={m} onSubmitScore={() => setScoreFor(m)} variant="accepted" />)}
          </ul>
        </Panel>
      )}
      {past.length > 0 && (
        <Panel testId="cl-matches-past">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-3">{t("cl.matches.pastKicker")} ({past.length})</div>
          <ul className="space-y-2">
            {past.slice(0, 20).map((m) => <MatchRow key={m.id} m={m} variant="past" />)}
          </ul>
        </Panel>
      )}
      {scoreFor && (
        <ScoreSubmitModal
          match={scoreFor}
          onClose={() => setScoreFor(null)}
          onSaved={() => { setScoreFor(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

function MatchRow({ m, onSubmitScore, variant }) {
  const { t } = useT();
  const peer = m.peer || {};
  const my = m.my_submission;
  const oppPresent = m.opp_submission_present;
  // Outcome (when completed/expired) — from viewer's perspective.
  let outcomeBadge = null;
  if (m.status === "completed" && m.final_home_score != null) {
    const myFinal = m.viewer_was_home ? m.final_home_score : m.final_away_score;
    const oppFinal = m.viewer_was_home ? m.final_away_score : m.final_home_score;
    let outcome = "D";
    if (myFinal > oppFinal) outcome = "W";
    else if (myFinal < oppFinal) outcome = "L";
    const cls = outcome === "W" ? "bg-[#00FF87] text-black" : outcome === "L" ? "bg-[#E11D48] text-white" : "bg-[#71717A] text-white";
    outcomeBadge = (
      <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] ${cls}`} data-testid={`cl-match-outcome-${m.id}`}>{myFinal}-{oppFinal} {outcome}</span>
    );
  }
  if (m.status === "expired") {
    outcomeBadge = <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] bg-[#71717A] text-white" data-testid={`cl-match-expired-${m.id}`}>{t("cl.matches.expired")}</span>;
  }

  const borderCls = {
    accepted: "border-[#7C3AED]",
    disputed: "border-[#E11D48]",
    past: m.status === "completed" ? "border-[#E4E4E7] dark:border-[#2A2A30]" : "border-[#71717A]",
  }[variant] || "border-[#E4E4E7]";

  return (
    <li className={`flex items-center justify-between gap-3 p-3 border ${borderCls}`} data-testid={`cl-match-${m.id}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Trophy className={`w-5 h-5 flex-shrink-0 ${variant === "disputed" ? "text-[#E11D48]" : variant === "past" ? "text-[#71717A]" : "text-[#7C3AED]"}`} />
        <div className="min-w-0">
          <div className="font-bold truncate">{peer.username || "?"}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] flex flex-wrap items-center gap-2">
            <span>{formatProposedAt(m.scheduled_at, t)}</span>
            {variant === "accepted" && my && !oppPresent && <span className="text-[#EA580C]">{t("cl.matches.awaitingOpp")}</span>}
            {variant === "accepted" && my && oppPresent && <span className="text-[#71717A]">{t("cl.matches.processing")}</span>}
            {variant === "accepted" && !my && oppPresent && <span className="text-[#EA580C]">{t("cl.matches.oppSubmitted")}</span>}
            {variant === "disputed" && <span className="text-[#E11D48]">{t("cl.matches.disputeBadge")}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
        {outcomeBadge}
        {(variant === "accepted" || variant === "disputed") && (
          <button
            type="button"
            onClick={onSubmitScore}
            data-testid={`cl-match-submit-${m.id}`}
            className="inline-flex items-center gap-1 bg-[#7C3AED] text-white hover:bg-[#6D28D9] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-2 transition-colors"
          >
            {my ? <><Edit3 className="w-3 h-3" /> {t("cl.matches.editScore")}</> : <><Send className="w-3 h-3" /> {t("cl.matches.submitScore")}</>}
          </button>
        )}
        {peer.id && (
          <Link to={`/chat/${peer.id}`} data-testid={`cl-match-chat-${m.id}`} className="inline-flex items-center gap-1 border border-[#7C3AED] text-[#7C3AED] hover:bg-[#7C3AED] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-2 transition-colors">
            <MessageSquare className="w-3 h-3" />
          </Link>
        )}
      </div>
    </li>
  );
}

function ScoreSubmitModal({ match, onClose, onSaved }) {
  const { t } = useT();
  const initial = match.my_submission || { my_score: "", opponent_score: "" };
  const [my, setMy] = useState(String(initial.my_score ?? ""));
  const [opp, setOpp] = useState(String(initial.opponent_score ?? ""));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    const m = parseInt(my, 10);
    const o = parseInt(opp, 10);
    if (Number.isNaN(m) || Number.isNaN(o) || m < 0 || o < 0 || m > 99 || o > 99) {
      toast.error(t("cl.score.errInvalid")); return;
    }
    setBusy(true);
    try {
      await api.post(`/classics/matches/${match.id}/submit`, { my_score: m, opponent_score: o });
      toast.success(t("cl.score.savedToast"));
      onSaved?.();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="cl-score-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md bg-white dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{match.game?.name?.toUpperCase()}</div>
            <h3 className="font-heading text-2xl uppercase tracking-wider">{t("cl.score.title", { name: match.peer?.username })}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="cl-score-close" className="text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="font-body text-xs text-[#71717A]">{t("cl.score.helper")}</p>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("cl.score.myScore")}</label>
              <input type="number" min="0" max="99" value={my} onChange={(e) => setMy(e.target.value)} required data-testid="cl-score-my" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-2xl font-heading focus:border-[#7C3AED] outline-none text-center text-[#09090B] dark:text-[#F4F4F5]" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("cl.score.oppScore")}</label>
              <input type="number" min="0" max="99" value={opp} onChange={(e) => setOpp(e.target.value)} required data-testid="cl-score-opp" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-2xl font-heading focus:border-[#7C3AED] outline-none text-center text-[#09090B] dark:text-[#F4F4F5]" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">{t("cl.request.cancel")}</button>
            <button type="submit" disabled={busy} data-testid="cl-score-save" className="inline-flex items-center gap-1 bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-40 font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
              <Send className="w-3 h-3" /> {busy ? t("cl.request.sending") : t("cl.score.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LeaderboardView({ gameId }) {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/classics/games/${gameId}/leaderboard?limit=20`)
      .then(({ data }) => { if (!cancelled) setRows(data || []); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [gameId]);

  if (loading) return <TactLoader label={t("cl.lb.loading")} />;
  if (rows.length === 0) {
    return (
      <Panel testId="cl-lb-empty">
        <div className="font-mono text-sm text-[#71717A]">[ {t("cl.lb.empty")} ]</div>
      </Panel>
    );
  }

  return (
    <Panel testId="cl-lb">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED] mb-3">{t("cl.lb.kicker")}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="cl-lb-table">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] border-b border-[#E4E4E7] dark:border-[#2A2A30]">
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">{t("cl.lb.player")}</th>
              <th className="px-2 py-2 text-center">P</th>
              <th className="px-2 py-2 text-center text-[#00FF87]">W</th>
              <th className="px-2 py-2 text-center">D</th>
              <th className="px-2 py-2 text-center text-[#E11D48]">L</th>
              <th className="px-2 py-2 text-center">GF</th>
              <th className="px-2 py-2 text-center">GA</th>
              <th className="px-2 py-2 text-center">GD</th>
              <th className="px-2 py-2 text-center">{t("cl.lb.form")}</th>
              <th className="px-2 py-2 text-right text-[#7C3AED]">PTS</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const RankIcon = r.rank === 1 ? Crown : r.rank === 2 ? Trophy : r.rank === 3 ? Award : null;
              const rankColor = r.rank === 1 ? "text-[#FFD700]" : r.rank === 2 ? "text-[#C0C0C0]" : r.rank === 3 ? "text-[#CD7F32]" : "text-[#71717A]";
              return (
                <tr key={r.user_id} className={`border-b border-[#E4E4E7]/50 dark:border-[#2A2A30]/50 ${r.rank === 1 ? "bg-[#FFD700]/5" : ""}`} data-testid={`cl-lb-row-${r.rank}`}>
                  <td className="px-2 py-2"><span className={`inline-flex items-center gap-1 font-mono text-xs ${rankColor}`}>{RankIcon && <RankIcon className="w-3 h-3" />}{r.rank}</span></td>
                  <td className="px-2 py-2 font-bold"><Link to={`/players/${r.username}`} className="hover:text-[#7C3AED] transition-colors">{r.username}</Link>{r.club_name && <span className="ml-2 font-mono text-[10px] text-[#71717A]">{r.club_name}</span>}</td>
                  <td className="px-2 py-2 text-center font-mono">{r.played}</td>
                  <td className="px-2 py-2 text-center font-mono text-[#00FF87]">{r.won}</td>
                  <td className="px-2 py-2 text-center font-mono">{r.drawn}</td>
                  <td className="px-2 py-2 text-center font-mono text-[#E11D48]">{r.lost}</td>
                  <td className="px-2 py-2 text-center font-mono">{r.gf}</td>
                  <td className="px-2 py-2 text-center font-mono">{r.ga}</td>
                  <td className="px-2 py-2 text-center font-mono">{r.gd > 0 ? `+${r.gd}` : r.gd}</td>
                  <td className="px-2 py-2 text-center">
                    <span className="inline-flex gap-0.5">
                      {(r.form || []).map((f, i) => (
                        <span key={i} className={`inline-block w-4 h-4 text-[9px] font-bold flex items-center justify-center ${f === "W" ? "bg-[#00FF87] text-black" : f === "L" ? "bg-[#E11D48] text-white" : "bg-[#71717A] text-white"}`}>{f}</span>
                      ))}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-heading text-lg text-[#7C3AED]">{r.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function RequestModal({ player, game, onClose, onSent }) {
  const { t } = useT();
  const [proposedAt, setProposedAt] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { receiver_id: player.user_id };
      if (proposedAt) payload.proposed_at = new Date(proposedAt).toISOString();
      if (message.trim()) payload.message = message.trim();
      await api.post(`/classics/games/${game.id}/request`, payload);
      toast.success(t("cl.request.sentToast", { name: player.username }));
      onSent?.();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="cl-request-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md bg-white dark:bg-[#0F0F12] border border-[#E4E4E7] dark:border-[#2A2A30] p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#7C3AED]">{game.name.toUpperCase()}</div>
            <h3 className="font-heading text-2xl uppercase tracking-wider">{t("cl.request.title", { name: player.username })}</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="cl-request-close" className="text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("cl.request.proposedAt")} <span className="text-[#71717A] normal-case font-normal">({t("cl.request.optional")})</span></label>
            <input type="datetime-local" value={proposedAt} onChange={(e) => setProposedAt(e.target.value)} data-testid="cl-request-datetime" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">{t("cl.request.message")} <span className="text-[#71717A] normal-case font-normal">({t("cl.request.optional")})</span></label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={3} placeholder={t("cl.request.messagePh")} data-testid="cl-request-message" className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
              {t("cl.request.cancel")}
            </button>
            <button type="submit" disabled={busy} data-testid="cl-request-send" className="inline-flex items-center gap-1 bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-40 font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
              <Send className="w-3 h-3" /> {busy ? t("cl.request.sending") : t("cl.request.send")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ====================================================================
// CLASSICS CHAT — per-game community room + pinned rules.
// ====================================================================
function ClassicsChatView({ game }) {
  const { t } = useT();
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const [pin, setPin] = useState(null);
  const [editingPin, setEditingPin] = useState(false);
  const [pinDraft, setPinDraft] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const feedRef = React.useRef(null);

  const refreshAll = React.useCallback(async () => {
    try {
      const [pinR, msgR] = await Promise.all([
        api.get(`/classics/games/${game.id}/chat/pin`),
        api.get(`/classics/games/${game.id}/chat`),
      ]);
      setPin(pinR.data);
      setMessages(msgR.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [game.id]);

  useEffect(() => { setLoading(true); refreshAll(); }, [refreshAll]);

  // Poll new messages every 8s using the `since` cursor.
  useEffect(() => {
    if (messages.length === 0) return undefined;
    const iv = setInterval(async () => {
      try {
        const last = messages[messages.length - 1]?.created_at;
        if (!last) return;
        const { data } = await api.get(`/classics/games/${game.id}/chat`, { params: { since: last } });
        if (data && data.length > 0) setMessages((prev) => [...prev, ...data]);
      } catch { /* noop */ }
    }, 8000);
    return () => clearInterval(iv);
  }, [game.id, messages]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages.length]);

  const onImagePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(f.type)) {
      toast.error(t("cl.chat.errImageType")); return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(t("cl.chat.errImageSize")); return;
    }
    setImageFile(f);
    const r = new FileReader();
    r.onload = () => setImagePreview(r.result);
    r.readAsDataURL(f);
  };

  const send = async (e) => {
    e.preventDefault();
    const t2 = text.trim();
    if (!t2 && !imageFile) return;
    setSending(true);
    try {
      const fd = new FormData();
      if (t2) fd.append("text", t2);
      if (imageFile) fd.append("image", imageFile);
      const { data } = await api.post(`/classics/games/${game.id}/chat`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessages((prev) => [...prev, data]);
      setText(""); setImageFile(null); setImagePreview(null);
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSending(false); }
  };

  const deleteMessage = async (m) => {
    if (!window.confirm(t("cl.chat.deleteConfirm"))) return;
    try {
      await api.delete(`/classics/games/${game.id}/chat/${m.id}`);
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, text: "", image_path: null, deleted_at: new Date().toISOString() } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePin = async () => {
    try {
      await api.put(`/classics/games/${game.id}/chat/pin`, { text: pinDraft });
      toast.success(t("cl.chat.pinSaved"));
      setEditingPin(false);
      await refreshAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const startEditPin = () => { setPinDraft(pin?.text || ""); setEditingPin(true); };

  if (loading) return <TactLoader label={t("cl.chat.loading")} />;

  return (
    <div className="space-y-3" data-testid="cl-chat">
      {/* Pinned rules */}
      {(pin?.text || isStaff) && (
        <Panel testId="cl-chat-pin" className="border-l-2 border-l-[#EA580C]">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <Pin className="w-4 h-4 text-[#EA580C] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#EA580C]">{t("cl.chat.pinKicker")}</div>
                {editingPin ? (
                  <textarea value={pinDraft} onChange={(e) => setPinDraft(e.target.value)} rows={3} maxLength={2000} data-testid="cl-chat-pin-input" className="mt-1 w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#EA580C] outline-none text-[#09090B] dark:text-[#F4F4F5]" />
                ) : pin?.text ? (
                  <p className="font-body text-sm whitespace-pre-wrap mt-1">{pin.text}</p>
                ) : (
                  <p className="font-body text-sm text-[#71717A] mt-1">{t("cl.chat.pinEmpty")}</p>
                )}
                {pin?.pinned_by && !editingPin && (
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-1">— {pin.pinned_by}</div>
                )}
              </div>
            </div>
            {isStaff && (
              <div className="flex items-center gap-1 flex-shrink-0">
                {editingPin ? (
                  <>
                    <button onClick={savePin} data-testid="cl-chat-pin-save" className="inline-flex items-center gap-1 bg-[#EA580C] text-white hover:bg-[#C2410C] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors">
                      <Check className="w-3 h-3" /> {t("cl.chat.pinSave")}
                    </button>
                    <button onClick={() => setEditingPin(false)} className="border border-[#71717A] text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5] font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors">{t("cl.chat.pinCancel")}</button>
                  </>
                ) : (
                  <button onClick={startEditPin} data-testid="cl-chat-pin-edit" className="inline-flex items-center gap-1 border border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C] hover:text-white font-mono text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 transition-colors">
                    <PencilLine className="w-3 h-3" /> {t("cl.chat.pinEdit")}
                  </button>
                )}
              </div>
            )}
          </div>
        </Panel>
      )}

      {/* Message feed */}
      <Panel testId="cl-chat-feed">
        <div ref={feedRef} className="max-h-[480px] overflow-y-auto space-y-2 pr-1" data-testid="cl-chat-messages">
          {messages.length === 0 ? (
            <div className="font-mono text-sm text-[#71717A] text-center py-8">[ {t("cl.chat.empty")} ]</div>
          ) : messages.map((m) => (
            <ChatMessage key={m.id} m={m} onDelete={deleteMessage} canDelete={isStaff || m.user_id === user?.id} />
          ))}
        </div>
      </Panel>

      {/* Composer */}
      <Panel testId="cl-chat-composer">
        <form onSubmit={send} className="space-y-2">
          {imagePreview && (
            <div className="relative inline-block">
              <img src={imagePreview} alt="preview" className="max-h-32 border border-[#E4E4E7] dark:border-[#2A2A30]" />
              <button type="button" onClick={() => { setImageFile(null); setImagePreview(null); }} data-testid="cl-chat-image-clear" className="absolute -top-2 -right-2 bg-[#E11D48] text-white w-6 h-6 flex items-center justify-center hover:bg-[#BE123C]">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <label className="cursor-pointer border border-[#E4E4E7] dark:border-[#2A2A30] text-[#71717A] hover:border-[#7C3AED] hover:text-[#7C3AED] p-2 transition-colors" data-testid="cl-chat-image-picker">
              <ImageIcon className="w-4 h-4" />
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onImagePick} className="hidden" />
            </label>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("cl.chat.placeholder")}
              data-testid="cl-chat-text"
              maxLength={2000}
              className="flex-1 bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 text-sm font-body focus:border-[#7C3AED] outline-none text-[#09090B] dark:text-[#F4F4F5]"
            />
            <button type="submit" disabled={sending || (!text.trim() && !imageFile)} data-testid="cl-chat-send" className="inline-flex items-center gap-1 bg-[#7C3AED] text-white hover:bg-[#6D28D9] disabled:opacity-40 font-mono text-[10px] uppercase tracking-[0.3em] px-4 py-2 transition-colors">
              <Send className="w-3 h-3" /> {sending ? t("cl.chat.sending") : t("cl.chat.send")}
            </button>
          </div>
        </form>
      </Panel>
    </div>
  );
}

function ChatMessage({ m, onDelete, canDelete }) {
  const { t } = useT();
  const isDeleted = !!m.deleted_at;
  return (
    <div className="flex items-start gap-2 p-2 border border-[#E4E4E7] dark:border-[#2A2A30] group" data-testid={`cl-chat-msg-${m.id}`}>
      <div className="w-8 h-8 rounded-full bg-[#7C3AED]/15 flex items-center justify-center font-bold text-[#7C3AED] flex-shrink-0 text-xs">
        {(m.username || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/players/${m.username}`} className="font-bold text-sm hover:text-[#7C3AED] transition-colors">{m.username || "?"}</Link>
          <span className="font-mono text-[10px] text-[#71717A]">{new Date(m.created_at).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}</span>
        </div>
        {isDeleted ? (
          <div className="font-mono text-xs text-[#71717A] italic mt-1">[ {t("cl.chat.deleted", { by: m.deleted_by_role || "owner" })} ]</div>
        ) : (
          <>
            {m.text && <p className="text-sm whitespace-pre-wrap break-words mt-1">{m.text}</p>}
            {m.image_path && (
              <div className="mt-1">
                <ProtectedImage path={m.image_path} alt="attachment" className="max-h-60 border border-[#E4E4E7] dark:border-[#2A2A30]" />
              </div>
            )}
          </>
        )}
      </div>
      {canDelete && !isDeleted && (
        <button type="button" onClick={() => onDelete(m)} data-testid={`cl-chat-msg-delete-${m.id}`} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#71717A] hover:text-[#E11D48] p-1">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

